import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, FileText, Loader2, X } from 'lucide-react';
import { lettersApi } from '../../services/api';
import { LetterDocument } from './LetterDocument';
import { formatISTDateTime } from '../../utils/dateTime';

interface TemplateRow {
  id: number;
  template_name: string;
  tags?: string[];
  updated_at?: string;
}

interface TemplateDetail {
  id: number;
  template_name: string;
  body_html: string;
  letterhead_html?: string;
  footer_html?: string;
  tags?: string[];
}

export function ViewTemplatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const previewRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<TemplateDetail | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await lettersApi.listTemplates();
      setTemplates(response.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleView = async (templateId: number) => {
    try {
      setPreviewLoading(true);
      const response = await lettersApi.getTemplateById(templateId);
      setPreviewTemplate(response.data || null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open template preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUseTemplate = (templateId: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', 'letters-create-fresh');
    params.set('useTemplateId', String(templateId));
    params.delete('letterId');
    params.delete('openLetterId');
    setSearchParams(params);
  };

  const sortedTemplates = useMemo(
    () => [...templates].sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || ''))),
    [templates]
  );

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">View Templates</h1>
          <p className="text-sm text-gray-600 mt-1">Browse saved templates and reuse them in Create Letter.</p>
        </div>
        <button
          onClick={loadTemplates}
          className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading templates...
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">⚠ {error}</div>
      ) : sortedTemplates.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-600">
          No templates available.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm table-fixed">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="w-[52%] text-left px-4 py-3 font-semibold text-gray-700">Template Name</th>
                <th className="w-[22%] text-left px-4 py-3 font-semibold text-gray-700">Updated</th>
                <th className="w-[26%] text-right px-4 py-3 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTemplates.map((template) => (
                <tr key={template.id} className="border-b border-gray-100 last:border-b-0">
                  <td className="px-4 py-3 text-gray-900 font-medium break-words whitespace-normal">{template.template_name}</td>
                  <td className="px-4 py-3 text-gray-600">{formatISTDateTime(template.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleView(template.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                      <button
                        onClick={() => handleUseTemplate(template.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700"
                      >
                        <FileText className="w-4 h-4" /> Use Template
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      )}

      {(previewLoading || previewTemplate) && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-6xl h-[90vh] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{previewTemplate?.template_name || 'Template Preview'}</h3>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6 bg-gray-50">
              {previewLoading ? (
                <div className="text-center text-gray-600 mt-20">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading preview...
                </div>
              ) : previewTemplate ? (
                <LetterDocument
                  ref={previewRef}
                  subject={previewTemplate.template_name}
                  letterDate={new Date().toISOString().slice(0, 10)}
                  bodyHtml={previewTemplate.body_html || '<p></p>'}
                  letterheadHtml={previewTemplate.letterhead_html || ''}
                  footerHtml={previewTemplate.footer_html || ''}
                  enableDraftSignatureDrag={false}
                  showPrintBoundaryOverlay={false}
                  fitViewport
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
